import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { CaretLeft, CaretRight, FilePdf, MapTrifold, House } from "@phosphor-icons/react";

const imgUrl = (rel) => {
  if (!rel) return "";
  try { return new URL(rel, API_BASE).toString(); } catch { return rel; }
};

export default function WalkthroughShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await api.get(`/walkthroughs/share/${token}`);
      setData(d);
    } catch (e) {
      setErr(e.response?.data?.detail || "Walkthrough not found or has expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (!data) return;
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min((data.steps.length - 1) || 0, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data]);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/walkthroughs/share/${token}/download.pdf`);
      if (!res.ok) throw new Error("PDF unavailable");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estima-walkthrough-${data.slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground" data-testid="walkthrough-share-loading">Loading…</div>;
  }
  if (err) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center" data-testid="walkthrough-share-error">
        <h1 className="font-serif text-3xl mb-3">Walkthrough not available</h1>
        <p className="text-muted-foreground max-w-md mb-6">{err}</p>
        <Link to="/" className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2">
          <House size={14} weight="duotone" /> Go to Estima home
        </Link>
      </div>
    );
  }
  if (!data) return null;

  const totalSteps = data.steps.length;
  const step = data.steps[idx];

  return (
    <div className="min-h-screen bg-background" data-testid="walkthrough-share-page">
      {/* Top bar */}
      <header className="border-b hairline">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="font-serif text-xl">Estima</Link>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={totalSteps === 0}
              className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="walkthrough-share-pdf"
            >
              <FilePdf size={14} weight="duotone" /> Download PDF
            </button>
            <Link
              to="/login"
              className="text-xs px-3 py-2 border border-[hsl(var(--secondary))] text-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))] hover:text-primary-foreground transition"
              data-testid="walkthrough-share-cta"
            >
              Try Estima free →
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="mb-6">
          <div className="eyebrow text-[hsl(var(--secondary))] mb-2 inline-flex items-center gap-2">
            <MapTrifold size={12} weight="duotone" /> {data.category}
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl leading-tight" data-testid="walkthrough-share-title">{data.title}</h1>
          {data.description && <p className="text-muted-foreground mt-3 max-w-2xl">{data.description}</p>}
        </div>

        {totalSteps === 0 ? (
          <div className="card-flat p-12 text-center text-muted-foreground" data-testid="walkthrough-share-empty">
            This walkthrough doesn't have any steps yet.
          </div>
        ) : (
          <>
            {/* Progress dots */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto" data-testid="walkthrough-share-progress">
              {data.steps.map((s, i) => (
                <button
                  key={s.id || `step-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 transition-all ${i === idx ? "w-10 bg-[hsl(var(--secondary))]" : "w-6 bg-muted"}`}
                  aria-label={`Step ${i + 1}`}
                  data-testid={`walkthrough-share-dot-${i + 1}`}
                />
              ))}
              <span className="ml-3 text-xs text-muted-foreground whitespace-nowrap">Step {idx + 1} of {totalSteps}</span>
            </div>

            {/* Slide */}
            <div className="card-flat p-0 overflow-hidden" data-testid={`walkthrough-share-step-${idx + 1}`}>
              {step.image_url && (
                <div className="bg-muted/30">
                  <img
                    src={imgUrl(step.image_url)}
                    alt={step.caption}
                    className="w-full h-auto block"
                    data-testid="walkthrough-share-image"
                  />
                </div>
              )}
              <div className="p-6 sm:p-8 border-t hairline">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))] mb-2">Step {idx + 1}</div>
                <p className="font-serif text-lg sm:text-xl leading-relaxed" data-testid="walkthrough-share-caption">{step.caption}</p>
              </div>
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className="text-sm px-4 py-2 border hairline inline-flex items-center gap-2 disabled:opacity-30"
                data-testid="walkthrough-share-prev"
              >
                <CaretLeft size={14} /> Previous
              </button>
              <span className="text-xs text-muted-foreground">←  → arrow keys</span>
              <button
                onClick={() => setIdx((i) => Math.min(totalSteps - 1, i + 1))}
                disabled={idx === totalSteps - 1}
                className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-30"
                data-testid="walkthrough-share-next"
              >
                Next <CaretRight size={14} />
              </button>
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 border-t hairline pt-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-muted-foreground">Like what you see? Estima has 15+ tools like this.</p>
            <Link to="/login" className="btn-primary px-5 py-2 text-sm" data-testid="walkthrough-share-bottom-cta">
              Start your free trial →
            </Link>
          </div>
        </div>

        <footer className="mt-10 pt-6 border-t hairline text-[11px] text-muted-foreground flex flex-col sm:flex-row gap-2 justify-between" data-testid="walkthrough-share-copyright">
          <div>© {new Date().getFullYear()} Estima — a personal property decision engine.</div>
          <div className="flex gap-4">
            <Link to="/legal/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
